import { useEffect, useState } from 'react';
import type { IServiceProvider } from '../../interfaces/serviceProvider';
import { ProcedureStatus } from '../../../../common/enums/procedureStatus';
import { ReportStatus } from '../../../../common/enums/reportStatus';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { diagnosticReportEditPerformerSchema } from '../../../../common/validation/diagnosticReport';
import { procedureEditPerformerSchema } from '../../../../common/validation/procedure';
import { requestEditRecipientSchema } from '../../../../common/validation/request';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { Button, ButtonContainer, Dropdown, DropdownSearch } from '../../elements/common';
import { DisplayMessage } from '../../elements/DisplayMessage';
import { FieldSet } from '../../elements/fieldset/FieldSet';
import { FormGroup } from '../../elements/forms/FormGroup';

interface IChangeServiceProviderProps {
    currentOrganizationId: string;
    currentPerformerId: string;
    dateSent?: string | null;
    diagnosticReportId?: string;
    procedureId?: string;
    reload: () => Promise<void>;
    requestId?: string;
    role: 'biometrist' | 'grader' | 'provider' | 'biograder';
    serviceId: string;
}

interface IPerformer {
    performerOrganizationId: string | null;
    performerPractitionerId: string | null;
}

export function ChangeServiceProvider(props: IChangeServiceProviderProps) {
    const [serviceProviders, setServiceProviders] = useState<IServiceProvider[]>([]);
    const [performer, setPerformer] = useState<IPerformer>({
        performerOrganizationId: null,
        performerPractitionerId: null,
    });
    const [procedure, setProcedure] = useState<{ status: ProcedureStatus } | undefined>();
    const [diagnosticReport, setDiagnosticReport] = useState<
        | {
              clarificationQAPerformer: string | null;
              clarificationQARequester: string | null;
              reviewStatus: ReportStatus;
          }
        | undefined
    >(undefined);
    const [message, setMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);

    useEffect(() => {
        if (props.procedureId) {
            getProcedure();
        }
    }, [props.procedureId]);

    useEffect(() => {
        if (props.diagnosticReportId) {
            getDiagnosticReport();
        }
    }, [props.diagnosticReportId]);

    return (
        <FieldSet>
            <FormGroup hasErrors={validationErrors.has('performerOrganizationId')}>
                <DropdownSearch
                    error={validationErrors.get('performerOrganizationId')}
                    loadOptions={async (input) => await getServiceProviders(input)}
                    onChange={async (value) => await handleChange('performerOrganizationId', value)}
                    placeholder={translateKey('common.selectItem', translateKey('organization.organization'))}
                    value={performer.performerOrganizationId}
                />
            </FormGroup>
            <FormGroup hasErrors={validationErrors.has('performerPractitionerId')}>
                <Dropdown
                    error={validationErrors.get('requesterPractitionerId')}
                    onChange={async (value) => await handleChange('performerPractitionerId', value)}
                    placeholder={translateKey('common.selectItem', translateKey('practitioner.practitioner'))}
                    selectOptions={getServiceProviderPractitionersOptions()}
                    value={performer.performerPractitionerId}
                />
            </FormGroup>
            {message && (
                <DisplayMessage
                    message={message}
                    messageType="info"
                    onClose={async () => setMessage(null)}
                />
            )}
            <ButtonContainer
                buttons={[
                    <Button
                        color="secondary"
                        disabled={!performer.performerOrganizationId || !performer.performerPractitionerId}
                        key="assign"
                        onClick={async () => await saveServiceProvider()}
                    >
                        {translateKey('request.assign')}
                    </Button>,
                ]}
                noMargin
            />
        </FieldSet>
    );

    async function handleChange<K extends keyof IPerformer>(name: K, value: IPerformer[K]) {
        setPerformer({ ...performer, [name]: value });
        if (name === 'performerOrganizationId' && value) {
            setPerformer({ ...performer, [name]: value, performerPractitionerId: null });
            let url = `/api/serviceProviders?serviceId=${props.serviceId}&organizationId=${value}&isActive=true`;
            if (props.role === 'biometrist') {
                url += '&isBiometrist=true';
            } else if (props.role === 'grader') {
                url += '&isGrader=true';
            } else if (props.role === 'biograder') {
                url += '&isBiometrist=true&isGrader=true';
            }
            const result = await fetchJson(url, 'GET');
            setServiceProviders(result.json.items);
        }
    }

    async function getServiceProviders(input: string) {
        let url = `/api/serviceProviders?serviceId=${props.serviceId}&searchOrganization=${input}&onlyOrganizationName=true&isActive=true`;
        if (props.role === 'biometrist') {
            url += '&isBiometrist=true';
        } else if (props.role === 'grader') {
            url += '&isGrader=true';
        } else if (props.role === 'biograder') {
            url += '&isBiometrist=true&isGrader=true';
        }

        const result = await fetchJson(url, 'GET');

        const options = result.json.items.map((serviceProvider: IServiceProvider) => ({
            label: serviceProvider.employment.organization.name,
            value: serviceProvider.employment.organization.id,
        }));

        return options;
    }

    function getServiceProviderPractitionersOptions() {
        const filteredServiceProviders = serviceProviders.filter(
            (serviceProvider) =>
                !(
                    serviceProvider.employment.practitioner.id === props.currentPerformerId &&
                    serviceProvider.employment.organization.id === props.currentOrganizationId
                )
        );

        return filteredServiceProviders.map((serviceProvider: IServiceProvider) => ({
            text: getDisplayName(serviceProvider.employment.practitioner),
            value: serviceProvider.employment.practitioner.id,
        }));
    }

    async function saveRecipientServiceProvider() {
        const recipient = {
            recipientOrganizationId: performer.performerOrganizationId,
            recipientPractitionerId: performer.performerPractitionerId,
        };
        const { errors, item } = validate(requestEditRecipientSchema, recipient);
        setValidationErrors(errors);
        if (errors.size > 0) {
            return false;
        }
        await fetchJson(`/api/requests/${props.requestId}/giveProcess`, 'POST', item);

        return true;
    }

    async function getProcedure() {
        const result = await fetchJson(`/api/procedures/${props.procedureId}?admin=true`, 'GET');
        setProcedure(result.json);
    }

    async function getDiagnosticReport() {
        const result = await fetchJson(`/api/diagnosticReports/${props.diagnosticReportId}`, 'GET');
        setDiagnosticReport(result.json);
    }

    async function saveServiceProvider() {
        setMessage(null);
        if (!props.procedureId && !props.diagnosticReportId) {
            if (await saveRecipientServiceProvider()) {
                setMessage(translateKey('serviceProvider.changedSuccess'));
                await props.reload();
            }
        } else {
            let url;
            let urlReportUpdate;
            let schema;
            if (props.role === 'biometrist') {
                if (procedure && procedure.status === ProcedureStatus.inProgress) {
                    if (!(await saveRecipientServiceProvider())) {
                        return;
                    }
                }
                url = `/api/procedures/${props.procedureId}/updatePerformer`;
                schema = procedureEditPerformerSchema;
            } else if (props.role === 'grader' || props.role === 'provider') {
                url = `/api/diagnosticReports/${props.diagnosticReportId}/updatePerformer`;
                schema = diagnosticReportEditPerformerSchema;
                if (
                    diagnosticReport &&
                    (diagnosticReport.reviewStatus === ReportStatus.draft ||
                        (diagnosticReport.reviewStatus === ReportStatus.open &&
                            diagnosticReport.clarificationQARequester &&
                            !diagnosticReport.clarificationQAPerformer))
                ) {
                    if (!(await saveRecipientServiceProvider())) {
                        return;
                    }
                }
            } else if (props.role === 'biograder') {
                if (procedure && procedure.status === ProcedureStatus.inProgress) {
                    if (!(await saveRecipientServiceProvider())) {
                        return;
                    }
                }
                url = `/api/procedures/${props.procedureId}/updatePerformer`;
                urlReportUpdate = `/api/diagnosticReports/${props.diagnosticReportId}/updatePerformer`;
                schema = procedureEditPerformerSchema;
            } else {
                throw new Error('Changing serviceProvider not possible');
            }

            const { errors, item } = validate(schema, performer);
            setValidationErrors(errors);
            if (errors.size > 0) {
                return;
            }
            await fetchJson(url, 'POST', item);
            if (urlReportUpdate) {
                await fetchJson(urlReportUpdate, 'POST', item);
            }
            setMessage(translateKey('serviceProvider.changedSuccess'));
            await props.reload();
        }
    }
}
